import { get, head, isNil } from 'lodash';
import React, { useMemo } from 'react';

import BaseScore from 'pages/Bulletin/components/BaseScore';
import { ChartData } from 'pages/Bulletin/types/chart-data';
import { HistoryOfChangesItem } from 'pages/Bulletin/types/history-of-changes';
import isEmptyRecursive from 'utils/is-empty-recursive';

interface Props {
    field: string;
    data: {
        source: string;
        value: {
            version: string;
            baseScore: number;
            vectorString: string;
            [key: string]: string | number | boolean;
        };
    }[];
}

const Cvss: React.FC<Props> = ({ field, data }) => {
    const version = useMemo(() => head(data)?.value?.version?.replace(/(\.0+)+$/, '') ?? '', [data]);

    const baseScoreData = useMemo(
        () =>
            data.map(({ source, value }) => {
                const { baseScore, vectorString, ...vectorItems } = value;

                const statistics = (historyData: HistoryOfChangesItem[]) => {
                    if (historyData.every(({ changes }) => isNil(get(changes, ['metrics', source, field])))) {
                        return null;
                    } else {
                        return historyData.reduce<ChartData>((output, { version, published, changes }) => {
                            const score: number =
                                get(changes, ['metrics', source, field, 'baseScore']) ?? output.at(-1)?.score ?? 0;
                            output.push({ version, score, published });
                            return output;
                        }, []);
                    }
                };

                return {
                    source,
                    statistics,
                    score: baseScore,
                    vector: vectorString,
                    parts: vectorItems
                };
            }),
        [data, field]
    );

    if (isEmptyRecursive(data)) {
        return null;
    }

    return <BaseScore label={`CVSS ${version}`} data={baseScoreData} />;
};

export default Cvss;
